import type { Plan, ShopMonth } from '../types'
import type { SpendFigure } from './api'
import type { RowAction, ReviewRow } from './statementImport'

/**
 * Spending per shop, from statement imports: what a person keeps in their own
 * plan (SM-19), what they share for anonymous statistics if they opt in
 * (SM-18; see the backend's SpendStatistics), and how the two are compared.
 *
 * Only rows that become a budget's or bill's actual count, as for merchant
 * names: spending at a shop, not a payment to a person. Rows already imported
 * are skipped (they aren't `new`), so overlapping statements never count a
 * payment twice; each import's totals are added to the month's.
 */

/** This import's spending per shop and calendar month. */
export function shopMonthsFrom(rows: ReviewRow[], actionFor: (row: ReviewRow) => RowAction): ShopMonth[] {
  const byKey = new Map<string, ShopMonth & { pence: number }>()
  for (const row of rows) {
    if (row.status !== 'new' || row.amount >= 0 || row.merchant.length < 3 || actionFor(row).kind !== 'actual') continue
    const month = row.date.slice(0, 7)
    const key = `${row.merchant}|${month}`
    const entry = byKey.get(key) ?? { merchant: row.merchant, month, visits: 0, amount: 0, pence: 0 }
    entry.visits += 1
    entry.pence += Math.round(-row.amount * 100)
    byKey.set(key, entry)
  }
  return [...byKey.values()].map(({ pence, ...e }) => ({ ...e, amount: pence / 100 }))
}

/** Adds an import's spending to what earlier imports recorded. */
export function mergeShopSpending(existing: ShopMonth[], added: ShopMonth[]): ShopMonth[] {
  const out = existing.map((e) => ({ ...e }))
  for (const a of added) {
    const same = out.find((e) => e.merchant === a.merchant && e.month === a.month)
    if (same) {
      same.visits += a.visits
      same.amount = Math.round((same.amount + a.amount) * 100) / 100
    } else {
      out.push({ ...a })
    }
  }
  return out
}

export type SpendingShare = Record<string, { key: string; visits: number; total: number }[]>

/**
 * What an import shares, when the person has opted in: whole pounds, and only
 * shops already in the shared catalogue — the server would drop anything else,
 * but it shouldn't travel in the first place.
 */
export function spendingToShare(
  rows: ReviewRow[],
  actionFor: (row: ReviewRow) => RowAction,
  catalogue: Set<string>,
): SpendingShare {
  const out: SpendingShare = {}
  for (const s of shopMonthsFrom(rows, actionFor)) {
    if (!catalogue.has(s.merchant)) continue
    ;(out[s.month] ??= []).push({ key: s.merchant, visits: s.visits, total: Math.round(s.amount) })
  }
  return out
}

/** A person's own spending in one month per shop, across all their accounts. */
export function ownSpending(plan: Plan, month: string): Map<string, { visits: number; amount: number }> {
  const out = new Map<string, { visits: number; amount: number }>()
  for (const account of plan.accounts) {
    for (const s of account.shopSpending ?? []) {
      if (s.month !== month) continue
      const sum = out.get(s.merchant) ?? { visits: 0, amount: 0 }
      out.set(s.merchant, { visits: sum.visits + s.visits, amount: Math.round((sum.amount + s.amount) * 100) / 100 })
    }
  }
  return out
}

export interface Comparison {
  /** Positive when they spent more than the average: 0.05 is 5% more. */
  difference: number
  /** Where they sit against the middle half of people. */
  band: 'below' | 'typical' | 'above'
}

/** How someone's month at a shop compares with its average (all in pence). */
export function compare(ownPence: number, figure: Pick<SpendFigure, 'mean' | 'p25' | 'p75'>): Comparison {
  return {
    difference: figure.mean > 0 ? ownPence / figure.mean - 1 : 0,
    band: ownPence < figure.p25 ? 'below' : ownPence > figure.p75 ? 'above' : 'typical',
  }
}

const PREFERENCE_KEY = 'snapshot-money:share-spending'

/** Off unless this browser has said yes: unlike names, spending is only shared by opting in. */
export function spendingSharePreferred(): boolean {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === 'yes'
  } catch {
    return false
  }
}

export function rememberSpendingShare(on: boolean) {
  try {
    if (on) localStorage.setItem(PREFERENCE_KEY, 'yes')
    else localStorage.removeItem(PREFERENCE_KEY)
  } catch {
    // Not remembered; the box starts unticked next time.
  }
}
